import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Form, Button } from 'react-bootstrap';
import { useAdminCreateUserByAdminMutation } from '../../slices/usersApiSlice';
import { toast } from 'react-toastify';
import FormContainer from '../../components/FormContainer';
import Loader from '../../components/Loader';
import Message from '../../components/Message';


export default function AdminCreateUserByAdminScreen() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    bizName: '',
    primaryEmail: '',
    secondaryEmail: '',
    primaryPhone: '',
    secondaryPhone: '',
    password: '',
    confirmPassword: '',
    isSubscribedToEmail: false,
    isSubscribedToText: false,
    isAdmin: false,
    adminNotes: ''
  });

  const { 
    firstName,
    lastName,
    bizName,
    primaryEmail,
    secondaryEmail,
    primaryPhone,
    secondaryPhone,
    password,
    confirmPassword,
    isSubscribedToEmail,
    isSubscribedToText,
    isAdmin,
    adminNotes
  } = formData;

  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [ createUser, { isLoading: isCreating, error } ] = useAdminCreateUserByAdminMutation();

  if (!userInfo) return <Loader />;

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await createUser({
        userId: userInfo._id,
        firstName, 
        lastName, 
        bizName, 
        primaryEmail, 
        secondaryEmail,
        primaryPhone,
        secondaryPhone,
        password, 
        confirmPassword, 
        isSubscribedToEmail, 
        isSubscribedToText, 
        isAdmin, 
        adminNotes 
      }).unwrap();
      toast.success(`Success: User ${firstName} ${lastName} has been added. Password reset e-mail sent to ${primaryEmail}.`);
      navigate(`/admin/all_users`);
    } catch (err) {
      toast.error(err?.data?.message || err.error || "Error: User not added. If error persists, contact website administrator.");
    }
  };

  const renderError = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err.data?.message) return err.data.message;
    if (err.error) return err.error;
    try {
      return JSON.stringify(err);
    } catch {
      console.log("ERR OBJECT:", err);
      return String(err);
    }
  };

  return (
    <>
      <Link to={`/admin/all_users`} className='btn btn-light' style={{ marginTop: '32px', marginBottom: '16px' }}>
        Return to All Users
      </Link>
      <FormContainer>
        <h1 style={{ marginTop: '32px' }}>Add User</h1>
        <h5>* = Required field</h5>
        {isCreating && <Loader />}
        { error && <Message variant='danger'>{renderError(error)}</Message> }
        <Form onSubmit={ submitHandler }>
          <Form.Group controlId='firstName' className='my-2 mb-3'>
            <Form.Label>*First Name</Form.Label>
            <Form.Control 
              type='text'
              placeholder="Enter first name"
              value={firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value }) }
            ></Form.Control>
          </Form.Group>
          <Form.Group controlId='lastName' className='my-2 mb-3'>
            <Form.Label>*Last Name</Form.Label>
            <Form.Control 
              type='text'
              placeholder="Enter last name"
              value={lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value }) }
            ></Form.Control>
          </Form.Group>
          <Form.Group controlId='bizName' className='my-2 mb-3'>
            <Form.Label>Business Name</Form.Label>
            <Form.Control 
              type='text'
              placeholder="Enter business name"
              value={bizName}
              onChange={(e) => setFormData({ ...formData, bizName: e.target.value }) }
            ></Form.Control>
          </Form.Group>
          <Form.Group controlId='primaryEmail' className='mt-2 mb-3'>
            <Form.Label>*Primary E-Mail (part of the 'sign_in' credentials)</Form.Label>
            <Form.Control 
              type='email'
              placeholder="Enter primary e-mail"
              value={primaryEmail}
              onChange={(e) => setFormData({ ...formData, primaryEmail: e.target.value }) }
            ></Form.Control>
          </Form.Group>
          <Form.Group controlId='secondaryEmail' className='mt-2 mb-3'>
            <Form.Label>Secondary E-Mail</Form.Label>
            <Form.Control 
              type='email'
              placeholder="Enter secondary e-mail"
              value={secondaryEmail}
              onChange={(e) => setFormData({ ...formData, secondaryEmail: e.target.value }) }
            ></Form.Control>
          </Form.Group>
          <Form.Group controlId='primaryPhone' className='mt-2 mb-3'>
            <Form.Label>Primary Phone</Form.Label>
            <Form.Control 
              type='phone'
              placeholder="Enter primary phone"
              value={primaryPhone}
              onChange={(e) => setFormData({ ...formData, primaryPhone: e.target.value }) }
            ></Form.Control>
          </Form.Group>
          <Form.Group controlId='secondaryPhone' className='mt-2 mb-3'>
            <Form.Label>Secondary Phone</Form.Label>
            <Form.Control 
              type='phone'
              placeholder="Enter secondary phone"
              value={secondaryPhone}
              onChange={(e) => setFormData({ ...formData, secondaryPhone: e.target.value }) }
            ></Form.Control>
          </Form.Group>
          <Form.Group controlId='password' className='my-2 mb-3'>
            <Form.Label>*Temporary Password</Form.Label>
            <Form.Label>(If left blank, a random password will be generated and saved automatically)</Form.Label>
            <Form.Control 
              type='password'
              placeholder="Enter temporary password"
              value={password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value }) }
            ></Form.Control>
          </Form.Group>
          <Form.Group controlId='confirmPassword' className='my-2 mb-3'>
            <Form.Label>*Confirm Password</Form.Label>
            <Form.Control 
              type='password'
              placeholder="Confirm temporary password"
              value={confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value }) }
            ></Form.Control>
          </Form.Group>
          <Form.Group controlId='isSubscribedToEmail' className='my-2 mb-3'>
            <Form.Check 
              type='checkbox'
              label="Is Subscribed To Promotional E-Mail Messaging"
              checked={isSubscribedToEmail}
              onChange={(e) => setFormData({ ...formData, isSubscribedToEmail: e.target.checked }) }
            ></Form.Check>
          </Form.Group>
          <Form.Group controlId='isSubscribedToText' className='my-2 mb-3'>
            <Form.Check 
              type='checkbox'
              label="Is Subscribed To Promotional Text Messaging"
              checked={isSubscribedToText}
              onChange={(e) => setFormData({ ...formData, isSubscribedToText: e.target.checked }) }
            ></Form.Check>
          </Form.Group>
          <Form.Group controlId='isAdmin' className='my-2 mb-3'>
            <Form.Check 
              type='checkbox'
              label="Is Administrator"
              checked={isAdmin}
              onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked }) }
            ></Form.Check>
          </Form.Group>
          <Form.Group controlId='adminNotes' className='my-2 mb-3'>
            <Form.Label>Administrator Notes</Form.Label>
            <Form.Control 
              as='textarea'
              rows={12}
              placeholder="Enter notes, if applicable"
              value={adminNotes}
              onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value }) }
            ></Form.Control>
          </Form.Group>
          <div className='d-flex justify-content-between' style={{ marginBottom: '32px' }}>
            <Link to={`/admin/all_users`} className='btn btn-light my-2 text-decoration-none'>
              Cancel
            </Link>
            <Button 
              type='submit'
              variant='primary'
              className='my-2'
            >
              Add User
            </Button>
          </div>
        </Form>
      </FormContainer>
    </>
  )
}